/**
 * LinkTelegram.js  —  Add this inside UserAccount.js ProfileTab
 *
 * Or import it as a standalone component into UserAccount.js
 *
 * WHAT IT DOES:
 *  1. Generates a random 6-char code
 *  2. Saves it to Firebase: linkCodes/{CODE} = { uid, createdAt }
 *  3. Shows user: "Send /link YOURCODE to @ClubSwapBot"
 *  4. Polls Firebase every 3 seconds to check if telegramLinked = true
 *  5. When linked: shows green success state
 *
 * ADD TO UserAccount.js ProfileTab — paste inside the <div style={S.card}> 
 * that has secLabel "Profile", after the wallet field:
 *
 *   import LinkTelegram from './LinkTelegram'
 *   ...
 *   <LinkTelegram uid={user.uid} profile={profile} />
 */

import React, { useState, useEffect, useRef } from 'react'
import { initializeApp, getApps }             from 'firebase/app'
import { getDatabase, ref, set, get, onValue, off } from 'firebase/database'

const FIREBASE_CONFIG = {
    apiKey:            'AIzaSyD10olyURDLvM2BdtkLTnwVVB98qfNiDcc',
    authDomain:        'clubswap-5a74a.firebaseapp.com',
    databaseURL:       'https://clubswap-5a74a-default-rtdb.firebaseio.com',
    projectId:         'clubswap-5a74a',
    storageBucket:     'clubswap-5a74a.firebasestorage.app',
    messagingSenderId: '274838532963',
    appId:             '1:274838532963:web:4044ba76f6192dcfdf5956',
}

function getDB() {
    const apps = getApps()
    const app  = apps.length ? apps[0] : initializeApp(FIREBASE_CONFIG)
    return getDatabase(app)
}

function genCode() {
    return Math.random().toString(36).slice(2, 8).toUpperCase()
}

// ── Replace 'ClubSwapBot' with your actual bot username from BotFather ──
const BOT_USERNAME = 'ClubSwapBot'

export default function LinkTelegram({ uid, profile }) {
    const [code,      setCode]      = useState(null)
    const [copied,    setCopied]    = useState(false)
    const [linked,    setLinked]    = useState(profile?.telegramLinked || false)
    const [loading,   setLoading]   = useState(false)
    const [expiresIn, setExpiresIn] = useState(null)
    const pollRef  = useRef(null)
    const timerRef = useRef(null)

    // Listen for telegramLinked becoming true
    useEffect(() => {
        if (!uid) return
        const db      = getDB()
        const profRef = ref(db, `users/${uid}/profile/telegramLinked`)
        onValue(profRef, snap => {
            if (snap.val() === true) {
                setLinked(true)
                setCode(null)
                clearInterval(timerRef.current)
            }
        })
        return () => off(profRef)
    }, [uid])

    async function generateCode() {
        setLoading(true)
        const newCode = genCode()
        const db = getDB()
        await set(ref(db, `linkCodes/${newCode}`), {
            uid,
            createdAt: Date.now(),
        })
        setCode(newCode)
        setLoading(false)

        // Countdown 10 minutes
        let secs = 600
        setExpiresIn(secs)
        clearInterval(timerRef.current)
        timerRef.current = setInterval(() => {
            secs -= 1
            setExpiresIn(secs)
            if (secs <= 0) {
                clearInterval(timerRef.current)
                setCode(null)
                setExpiresIn(null)
            }
        }, 1000)
    }

    function copyCode() {
        if (code) {
            navigator.clipboard && navigator.clipboard.writeText(`/link ${code}`)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    function fmtExpiry(s) {
        const m = Math.floor(s / 60)
        const sec = s % 60
        return `${m}:${sec < 10 ? '0' : ''}${sec}`
    }

    // Already linked
    if (linked || profile?.telegramLinked) {
        return (
            <div style={styles.successBox}>
                <div style={styles.successRow}>
                    <span style={{ fontSize: 22 }}>✅</span>
                    <div>
                        <div style={styles.successTitle}>Telegram linked</div>
                        <div style={styles.successSub}>You'll receive notifications via @{BOT_USERNAME}</div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div style={styles.wrap}>
            <div style={styles.header}>
                <span style={{ fontSize: 20 }}>✈️</span>
                <div>
                    <div style={styles.title}>Link Telegram</div>
                    <div style={styles.sub}>Get instant notifications for matches, tournaments and payouts</div>
                </div>
            </div>

            {!code ? (
                <button style={styles.btn} onClick={generateCode} disabled={loading}>
                    {loading ? 'Generating…' : '🔗 Generate link code'}
                </button>
            ) : (
                <>
                    <div style={styles.steps}>
                        <div style={styles.step}>
                            <span style={styles.stepNum}>1</span>
                            <span>Open Telegram and search <strong>@{BOT_USERNAME}</strong></span>
                        </div>
                        <div style={styles.step}>
                            <span style={styles.stepNum}>2</span>
                            <span>Send this command:</span>
                        </div>
                    </div>

                    <div style={styles.codeBox}>
                        <div style={styles.codeText}>/link {code}</div>
                        <button style={styles.copyBtn} onClick={copyCode}>
                            {copied ? '✓ Copied' : '📋 Copy'}
                        </button>
                    </div>

                    <div style={styles.orRow}>
                        <a
                            href={`https://t.me/${BOT_USERNAME}?start=link_${code}`}
                            target="_blank"
                            rel="noreferrer"
                            style={styles.openTgBtn}
                        >
                            Open in Telegram →
                        </a>
                    </div>

                    <div style={styles.expiry}>
                        ⏱ Code expires in {fmtExpiry(expiresIn)} · Waiting for you to send it…
                    </div>

                    <div style={styles.pulseRow}>
                        <div style={styles.pulseDot} />
                        <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                            Listening for link confirmation
                        </span>
                    </div>
                </>
            )}
        </div>
    )
}

const styles = {
    wrap: {
        background:   'var(--color-background-secondary)',
        border:       '0.5px solid var(--color-border-secondary)',
        borderRadius: 10,
        padding:      '14px 16px',
        marginTop:    12,
    },
    header: { display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12 },
    title:  { fontSize: 14, fontWeight: 500, color: 'var(--color-text-primary)' },
    sub:    { fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 },

    btn: {
        width: '100%', padding: '10px', background: '#229ED9', color: 'white',
        border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500,
        cursor: 'pointer', fontFamily: 'var(--font-sans)',
    },

    steps:   { marginBottom: 10 },
    step:    { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 13, color: 'var(--color-text-secondary)' },
    stepNum: { width: 20, height: 20, borderRadius: '50%', background: '#229ED9', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 500, flexShrink: 0 },

    codeBox: {
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'var(--color-background-primary)',
        border: '1.5px solid #229ED9',
        borderRadius: 8, padding: '10px 14px', marginBottom: 10,
    },
    codeText:  { flex: 1, fontSize: 17, fontWeight: 500, color: '#229ED9', fontFamily: 'monospace', letterSpacing: 1 },
    copyBtn:   { padding: '5px 10px', background: '#E8F5FF', border: '0.5px solid #229ED9', borderRadius: 6, fontSize: 12, color: '#0A6FA0', cursor: 'pointer' },

    orRow:     { textAlign: 'center', marginBottom: 10 },
    openTgBtn: { display: 'inline-block', padding: '8px 16px', background: '#229ED9', color: 'white', borderRadius: 8, fontSize: 13, fontWeight: 500, textDecoration: 'none' },

    expiry:    { fontSize: 12, color: 'var(--color-text-secondary)', textAlign: 'center', marginBottom: 8 },

    pulseRow:  { display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' },
    pulseDot:  { width: 8, height: 8, borderRadius: '50%', background: '#229ED9', animation: 'pulse 1.4s ease-in-out infinite' },

    successBox:   { background: '#E1F5EE', border: '0.5px solid #5DCAA5', borderRadius: 10, padding: '12px 14px', marginTop: 12 },
    successRow:   { display: 'flex', alignItems: 'center', gap: 10 },
    successTitle: { fontSize: 14, fontWeight: 500, color: '#085041' },
    successSub:   { fontSize: 12, color: '#0F6E56', marginTop: 2 },
}
